/**
 * The files inside a message bubble (#1069 Story 4.2 Tasks 3-5; re-shaped by
 * Story 8.5).
 *
 * ⚠️ ONE LIST, IN THE SERVER'S ORDER (Story 8.5, AC 1). `attachments` arrives
 * sorted and nothing here re-sorts it. Before 8.5 this drew two lists -- a
 * two-column grid of 92pt image tiles with a `+N` scrim, then up to three
 * document chips -- because a photograph and a spreadsheet were held to be
 * different kinds of thing. The owner ruled otherwise: a file on a message
 * should look like a file on the request, and "all items", photographs
 * included. So every file is one `AttachmentRow`.
 *
 * ⚠️ AND THAT IS WHY THE INDEX MAPPING BELOW EXISTS, WHICH IS THE ONE PIECE OF
 * REAL DIFFICULTY IN THIS FILE (Story 8.5, C5). The viewer still pages images
 * as a set and opens a document alone, so `CorrespondencePanel.openAttachment`
 * still takes an index into `partitionForDisplay`'s `pictures` or `documents`
 * -- and a row's position in what the reader SEES is no longer its position in
 * either. For `[heic, jpg, pdf]` the rows are 0,1,2 while `pictures` is
 * `[jpg]` and `documents` is `[heic, pdf]`: pressing row 1 must report 0.
 *
 * ⚠️ THE MAPPING IS HERE AND NOT IN THE PANEL, DELIBERATELY (D4). The panel
 * re-derives the same partition to open the file; moving the mapping there
 * would put the DRAWING order and the INDEX derivation in two files again,
 * which is the defect Story 7.4's D3 closed. The component that draws the list
 * is the only one that knows what the reader pressed.
 *
 * ⚠️ THE TWO OVERFLOWS BECAME ONE, AND IT EXPANDS RATHER THAN OPENS (AC 2).
 * A tile was picked by LOOKING, so its overflow opened the viewer and the rest
 * were reachable by swiping. A row is picked by READING ITS NAME -- true of a
 * photograph's name too, now that it has one on screen -- so hiding names
 * behind a viewer would hide the only thing that identifies them. One cap,
 * `MAX_ROWS`, counting files of every kind, and the overflow expands in place.
 *
 * ⚠️ NEVER SIZE FROM THE IMAGE (4.2 D3). A photo's intrinsic ratio is unknown
 * until it loads, and adopting it late changes the bubble's height and shifts
 * the thread under whoever is reading -- the exact jump `MESSAGE_LIST_SETTLE_MS`
 * exists to suppress. A 40pt box declares its size up front, so nothing
 * reflows as thumbnails paint.
 */
import React, { useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import {
  AttachmentRow,
  ROW_MAX_WIDTH,
  partitionForDisplay,
} from './attachmentPieces';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';

import type { MessageFile } from './correspondence.types';

/**
 * How many file rows show before the overflow line (AC 2).
 *
 * ⚠️ IT COUNTS FILES OF EVERY KIND, WHICH `MAX_TILES` AND `MAX_CHIPS` DID NOT.
 * Six files is 308pt of rows where it used to be 188pt of tiles, and this cap
 * is what keeps a bubble sane: three rows plus a line of text. The owner ruled
 * for rows knowing they are taller, and the cap is the half of that ruling
 * that pays for it.
 */
const MAX_ROWS = 3;

type Props = {
  files: readonly MessageFile[];
  /** Open the viewer at this index into the IMAGE list (AC 12). */
  onOpen?: (imageIndex: number) => void;
  /** Open a document (AC 12). Index is into the DOCUMENT list. */
  onOpenDocument?: (documentIndex: number) => void;
  /**
   * 4.2 C3: a row is a `Pressable` CHILD, so its press does not bubble to the
   * bubble's own long-press. Without this forwarding, a files-only message is
   * the one message on native with no way to reach the actions chip. It is
   * forwarded for every file now, photographs included.
   */
  onLongPress?: () => void;
};

export const MessageAttachments = ({
  files,
  onOpen,
  onOpenDocument,
  onLongPress,
}: Props) => {
  const [expanded, setExpanded] = useState(false);

  if (files.length === 0) return null;

  /**
   * ⚠️ NOT DRAWN FROM, INDEXED FROM (C5). The partition rule is shared with
   * the strip so the two surfaces cannot disagree about which half a file
   * falls in; here its only job is to turn "the row the reader pressed" into
   * the index the viewer expects. `indexOf` on the file OBJECT, not on its id:
   * `partitionForDisplay` filters the same array, so the identities are the
   * very ones in `files`.
   */
  const { pictures, documents } = partitionForDisplay(files);

  const visible = expanded ? files : files.slice(0, MAX_ROWS);
  const hidden = files.length - visible.length;
  /**
   * ⚠️ THE SINGULAR IS THE COMMON CASE HERE, NOT THE EDGE (code review,
   * 2026-09-21). `MAX_ROWS = 3` counts files of EVERY kind, where 4.2's caps
   * were per kind (4 tiles, 3 chips) -- so a four-file message, which is
   * ordinary and is the panel suite's own `m-1` fixture, overflows by exactly
   * one. "+1 more files" was the first thing most readers would have seen.
   */
  const fileWord = hidden === 1 ? 'file' : 'files';

  return (
    <View style={styles.attachments} testID="message-attachments">
      {visible.map(file => {
        const pictureIndex = pictures.indexOf(file);

        return (
          <AttachmentRow
            key={file.id}
            file={file}
            testID={`message-file-${file.id}`}
            // ⚠️ THE BUBBLE'S WIDTH, AND IT IS THE BUBBLE'S ALONE (code
            // review, 2026-09-14; kept through 8.5 as C6; CORRECTED from a
            // ceiling to a width 2026-09-22 on the Epic 8 walk). The container
            // is `alignItems: 'flex-start'`, so the row is sized by its own
            // content rather than stretched, and `file_name` allows 255
            // characters -- one long name used to drag the row and its bubble
            // far past everything beside it, which is why a policy exists here
            // at all. The STRIP passes `alignSelf: 'stretch'` instead, which
            // is why the policy is a prop.
            //
            // ⚠️ THE OLD `maxWidth` FORM WAS WRONG IN THE OTHER DIRECTION AND
            // THIS COMMENT ASSERTED THE OPPOSITE OF WHAT IT MEASURED. It said
            // the name column's `flex: 1` "resolves to the text's own width".
            // Measured 2026-09-22 on a 411dp Android emulator through
            // `uiautomator dump`, and reproduced on iOS: it resolves to 34dp.
            // A ceiling caps and never grows, so with the bubble shrink-to-fit
            // nothing ever asked for the 188 and the bubble sized itself to
            // the message body instead. A definite width fixes it.
            style={styles.rowInBubble}
            // ~140pt of name column at 14 (see RowProps.nameMaxChars).
            nameMaxChars={20}
            // ⚠️ NO OPENER, NO PRESS (#1069 offline state): the panel
            // withholds both while the device is offline, and the row then
            // stays visible but inert rather than taking a tap that does
            // nothing.
            onPress={
              pictureIndex >= 0
                ? onOpen && (() => onOpen(pictureIndex))
                : onOpenDocument &&
                  (() => onOpenDocument(documents.indexOf(file)))
            }
            onLongPress={onLongPress}
          />
        );
      })}

      {hidden > 0 ? (
        <TouchableOpacity
          testID="message-files-overflow"
          style={styles.overflow}
          onPress={() => setExpanded(true)}
          onLongPress={onLongPress}
          accessibilityRole="button"
          accessibilityLabel={`Show ${hidden} more ${fileWord}`}
        >
          <Text
            style={styles.overflowText}
          >{`+${hidden} more ${fileWord}`}</Text>
        </TouchableOpacity>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  attachments: { gap: spacing.xs, alignItems: 'flex-start' },
  /**
   * See the note at the call site. A bubble rule, not a row rule.
   *
   * ⚠️ `width`, NOT `maxWidth` (fixed 2026-09-22, found on the Epic 8 walk).
   * A ceiling caps and never grows, and the bubble is shrink-to-fit, so
   * nothing in the chain ever ASKED for the 188: the row collapsed to its
   * children's minimums and the bubble sized itself to the message body.
   * Measured on an Android emulator at 411dp and reproduced on iOS: the whole
   * bubble came out 82dp wide, the name column 34dp, and `IMG_0317.jpeg`
   * rendered as `IM...` with the extension gone -- which is the one thing
   * `formatFileName`'s middle truncation exists to keep. A definite width
   * gives the row's `flex: 1` text column something to resolve against, which
   * is the 140 the call site has claimed all along.
   */
  rowInBubble: { width: ROW_MAX_WIDTH },
  /**
   * ⚠️ TEXT ALONE: NO FILL, NO BORDER, NO CHIP (Story 8.5). It used to borrow
   * the chip's metrics -- hairline, radius, muted ground -- because it sat in a
   * list of chips. It sits in a list of rows that have no fill, so a bordered
   * pill here would be the only enclosed thing in the bubble. `textLink`
   * measures 6.73 / 7.39 on the two bubbles, and the 44 floor keeps it a real
   * tap target without a box around it.
   */
  overflow: {
    justifyContent: 'center',
    minHeight: spacing.minimumTapTarget,
    maxWidth: ROW_MAX_WIDTH,
  },
  overflowText: { fontSize: 14, color: colors.textLink },
});
